import { useEffect, useRef, useState } from 'react';

import { Link } from 'react-router-dom';

import {
  LayoutGrid,
  ArrowRight, ArrowLeft, CheckCircle,
  ShoppingCart, Users, Coins, RefreshCw,
  FileCheck, Database, Lock, Brain, TrendingUp,
  Network, Settings, CreditCard, BarChart3
} from 'lucide-react';

import { ContactSection } from '../components/ContactSection';



/* ─── Primitives ──────────────────────────────── */

type Particle = { x: number; y: number; vx: number; vy: number; r: number; pulse: number };



const ParticleCanvas = ({ color = '#ff5d22', count = 50 }: { color?: string; count?: number }) => {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let w: number, h: number, pts: Particle[], raf: number;
    const init = () => {
      w = canvas.width = canvas.offsetWidth; h = canvas.height = canvas.offsetHeight;
      pts = Array.from({ length: count }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - .5) * .45, vy: (Math.random() - .5) * .45,
        r: Math.random() * 1.6 + .4, pulse: Math.random() * Math.PI * 2,
      }));
    };
    const [r, g, b] = [1, 3, 5].map((_, i) => parseInt(color.slice(1 + i * 2, 3 + i * 2), 16));
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.pulse += .02;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
      });
      for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
        const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
        if (d < 110) {
          ctx.beginPath(); ctx.strokeStyle = `rgba(${r},${g},${b},${(1 - d / 110) * .2})`;
          ctx.lineWidth = .6; ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y); ctx.stroke();
        }
      }
      pts.forEach(p => {
        const pr = p.r * (1 + .28 * Math.sin(p.pulse));
        const g2 = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, pr * 3);
        g2.addColorStop(0, `rgba(${r},${g},${b},.85)`); g2.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.beginPath(); ctx.arc(p.x, p.y, pr * 3, 0, Math.PI * 2); ctx.fillStyle = g2; ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    init(); draw();
    const ro = new ResizeObserver(init); ro.observe(canvas);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [color, count]);
  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />;
};



const Orb = ({ style, color, size, dur, delay = 0 }: {
  style?: React.CSSProperties; color: string; size: number; dur: number; delay?: number;
}) => (
  <div style={{
    position: 'absolute', borderRadius: '50%', filter: `blur(${size * .33}px)`,
    width: size, height: size, background: color, opacity: .12,
    animation: `caOrbFloat ${dur}s ease-in-out ${delay}s infinite`, ...style
  }} />
);

const Reveal = ({ children, delay = 0, className = '' }: {
  children: React.ReactNode; delay?: number; className?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: .08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={className} style={{
      opacity: vis ? 1 : 0, transform: vis ? 'translateY(0) scale(1)' : 'translateY(24px) scale(.97)',
      transition: `opacity .6s ease ${delay}s, transform .6s ease ${delay}s`,
    }}>{children}</div>
  );
};



const TCard = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div ref={ref} className={className} style={{ transition: 'transform .25s ease', transformStyle: 'preserve-3d' }}
      onMouseMove={e => {
        if (!ref.current) return;
        const r = ref.current.getBoundingClientRect();
        ref.current.style.transform = `perspective(800px) rotateY(${((e.clientX - r.left) / r.width - .5) * 10}deg) rotateX(${((e.clientY - r.top) / r.height - .5) * -10}deg) translateY(-4px)`;
      }}
      onMouseLeave={() => { if (ref.current) ref.current.style.transform = 'perspective(800px) rotateY(0) rotateX(0) translateY(0)'; }}>
      {children}
    </div>
  );
};



const FaqItem = ({ q, a, i }: { q: string; a: string; i: number }) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={i * .07}>
      <div onClick={() => setOpen(!open)} className="cursor-pointer mb-3 rounded-2xl border transition-all duration-300"
        style={{
          background: open ? 'linear-gradient(135deg,#0f0f0f,#191919)' : '#fff',
          borderColor: open ? 'rgba(227,30,36,.4)' : '#e5e7eb',
          boxShadow: open ? '0 8px 32px rgba(227,30,36,.1)' : '0 1px 6px rgba(0,0,0,.04)',
          padding: '13px 16px'
        }}>
        <div className="flex items-center justify-between gap-4">
          <span style={{ fontWeight: 700, fontSize: 13, color: open ? '#fff' : '#111' }}>{q}</span>
          <div style={{
            width: 22, height: 22, borderRadius: '50%', background: open ? '#ff5d22' : '#f3f4f6',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            transition: 'all .24s ease', transform: open ? 'rotate(45deg)' : 'none'
          }}>
            <svg width={8} height={8} viewBox="0 0 14 14" fill="none" stroke={open ? '#fff' : '#555'} strokeWidth={2.5}><path d="M7 1v12M1 7h12" /></svg>
          </div>
        </div>
        <div style={{ overflow: 'hidden', maxHeight: open ? 200 : 0, transition: 'max-height .34s ease', marginTop: open ? 9 : 0 }}>
          <p style={{ fontSize: 12.5, lineHeight: 1.65, color: 'rgba(255,255,255,.68)', margin: 0 }}>{a}</p>
        </div>
      </div>
    </Reveal>
  );
};



/* ─── Page data ──────────────────────────────── */

const CARDS = [
  {
    slug: 'cloud-channel-commerce',
    title: 'Cloud & Channel Commerce Engine',
    tagline: 'Launch, manage, and scale cloud, SaaS, UCaaS, and telecom services through a unified marketplace.',
    // ✅ icon was already present on card 1
    icon: LayoutGrid,

    tags: [
      'Cloud Distribution',
      'CSP',
      'Marketplace'
    ],

    overview:
      'AppGallop enables cloud distributors, telecom providers, and aggregators to commercialize Microsoft, AWS, Google Cloud, UCaaS, CCaaS, and SaaS offerings through a single partner-ready commerce platform.',

    bullets: [
      'Multi-vendor cloud marketplace management',
      'Microsoft CSP catalog automation',
      'AWS and Google Cloud distribution',
      'Partner self-service storefronts',
      'Subscription lifecycle management',
      'Automated provisioning orchestration'
    ],

    stats: [
      { v: '5000+', l: 'Cloud SKUs' },
      { v: '3000+', l: 'Partners' },
      { v: '<60s', l: 'Activation Time' }
    ],
    howItWorks: [
      { step: '01', title: 'Catalog Ingestion', desc: 'Vendor catalogs are ingested via API or file upload, normalised, and published to your branded storefront automatically.' },
      { step: '02', title: 'Partner Onboarding', desc: 'Partners self-register through the white-label portal, complete KYC, and receive tier-appropriate pricing and product access.' },
      { step: '03', title: 'Commerce Execution', desc: 'Customers browse, configure, and purchase. Orders trigger automated provisioning and billing without manual steps.' },
      { step: '04', title: 'Lifecycle Management', desc: 'Subscriptions, upgrades, renewals, and cancellations flow through automated lifecycle rules with full audit trails.' },
    ],
    techSpecs: [
      { label: 'Catalog sources', value: 'Microsoft, AWS, Google, Cisco + custom feeds' },
      { label: 'Storefront type', value: 'White-label, multi-domain, fully brandable' },
      { label: 'Pricing models', value: 'Fixed, tiered, usage-based, bundle, promotional' },
      { label: 'Provisioning', value: 'Sub-60s automated activation via REST API' },
      { label: 'Partner portal', value: 'Self-serve with deal reg, co-sell, and reporting' },
      { label: 'Certifications', value: 'SOC 2 Type II, ISO 27001, GDPR compliant' },
    ],
    capabilities: [
      { icon: ShoppingCart, title: 'Multi-Vendor Storefront', desc: 'Consolidate hundreds of vendor SKUs into a single branded storefront with intelligent search, filtering, and recommendations.' },
      { icon: Users, title: 'Partner Ecosystem Ops', desc: 'Manage partner tiers, incentives, deal registration, and co-sell activity from a unified partner relationship management layer.' },
      { icon: RefreshCw, title: 'Subscription Automation', desc: 'Handle trials, activations, upgrades, renewals, and cancellations automatically with configurable lifecycle rule sets.' },
      { icon: Settings, title: 'No-Code Catalog Builder', desc: 'Build and update product bundles, pricing rules, and promotional configurations without engineering involvement.' },
    ],
    faqs: [
      { q: 'Can we white-label the storefront for each partner tier?', a: 'Yes. Each partner tier gets a fully branded portal with your domain, logo, and color scheme. AppGallop remains invisible to your partners and customers.' },
      { q: 'How long does it take to go live?', a: 'Most deployments are live within 6 weeks. The high-velocity framework handles catalog setup, partner portal configuration, and vendor integrations end to end.' },
      { q: 'Can we support multiple storefronts for different regions or brands?', a: 'Fully supported. Each storefront has its own domain, catalog visibility, pricing tier, currency, and tax configuration. All managed from one admin layer.' },
    ],
  },

  {
    slug: 'financial-operations-core',
    title: 'Telecom & Subscription Billing Core',
    tagline:
      'Monetize recurring, usage-based, telecom, UCaaS, and CCaaS services with complete financial control.',
    // ✅ FIX: icon was missing on card 2 — added CreditCard
    icon: CreditCard,

    tags: [
      'Billing',
      'Telecom',
      'Monetization'
    ],

    overview:
      'Designed for cloud distributors, telecom operators, UCaaS providers, and SaaS businesses, AppGallop automates rating, charging, invoicing, taxation, settlements, and revenue recognition.',

    bullets: [
      'Subscription and usage-based billing',
      'Automated invoice generation and dispatch',
      'Multi-currency and multi-entity support',
      'Tax engine integration (Avalara, Vertex)',
      'ASC 606 / IFRS 15 revenue recognition',
      'ERP connectors (SAP, Oracle, NetSuite)'
    ],

    stats: [
      { v: '99.999%', l: 'Billing Accuracy' },
      { v: '160+', l: 'Tax Jurisdictions' },
      { v: '0', l: 'Manual Reconciliation' }
    ],
    howItWorks: [
      { step: '01', title: 'Event Capture', desc: 'Every billable event — order, usage record, renewal, adjustment — is captured with full commercial context.' },
      { step: '02', title: 'Rating & Pricing', desc: 'The billing engine applies the correct rate plan, discounts, promotions, and tax rules per customer and jurisdiction.' },
      { step: '03', title: 'Invoice Generation', desc: 'Invoices are generated, formatted, and dispatched automatically on the configured billing cycle with full line-item detail.' },
      { step: '04', title: 'GL Sync', desc: 'Recognised revenue is posted to the correct GL account in real time. Deferred amounts are released on schedule with no manual intervention.' },
    ],
    techSpecs: [
      { label: 'Billing models', value: 'Subscription, usage, hybrid, milestone, prepaid' },
      { label: 'ERP connectors', value: 'SAP S/4HANA, Oracle, NetSuite, Dynamics 365' },
      { label: 'Tax engines', value: 'Avalara, Vertex, native GST/VAT/TDS/WHT rules' },
      { label: 'Standards', value: 'ASC 606, IFRS 15, GAAP-aligned postings' },
      { label: 'Currencies', value: '160+ with real-time FX conversion' },
      { label: 'Certifications', value: 'SOC 2 Type II, ISO 27001, GDPR' },
    ],
    capabilities: [
      { icon: Coins, title: 'Multi-Entity Billing', desc: 'Bill across multiple legal entities, currencies, and tax regimes from a single engine with per-entity chart of accounts.' },
      { icon: FileCheck, title: 'Revenue Recognition', desc: 'Policy-driven schedules per contract type ensure recognised vs deferred revenue is always accurate and audit-ready.' },
      { icon: Database, title: 'Immutable Audit Trail', desc: 'Every billing event, adjustment, and GL posting is written to an append-only ledger — fully queryable and tamper-proof.' },
      { icon: Lock, title: 'Compliance Governance', desc: 'Tax filings, revenue schedules, and compliance reports are generated automatically with zero manual preparation.' },
    ],
    faqs: [
      { q: 'How does the platform handle mid-cycle subscription changes?', a: 'AppGallop calculates proration automatically based on the contract terms. Credits and debits are applied to the next invoice with full line-item transparency.' },
      { q: 'Can we support multiple billing frequencies per customer?', a: 'Yes. Each subscription line can have its own billing frequency — monthly, quarterly, annual, or usage-triggered — all consolidated into a single invoice per entity.' },
      { q: 'How are tax rule changes handled when regulations update?', a: 'AppGallop maintains a managed tax content library updated continuously through Avalara and Vertex. Rule changes are applied automatically without platform updates.' },
    ],
  },

  {
    slug: 'intelligence-growth-engine',
    title: 'Partner Intelligence & Revenue Growth',
    tagline:
      'Turn cloud, telecom, and subscription data into predictable growth.',
    // ✅ FIX: icon was missing on card 3 — added BarChart3
    icon: BarChart3,

    tags: [
      'AI',
      'Analytics',
      'Renewals'
    ],

    overview:
      'AppGallop analyzes consumption, subscription behavior, partner performance, and revenue trends to uncover growth opportunities, reduce churn, and improve renewals.',

    bullets: [
      'AI-powered churn prediction and scoring',
      'Revenue forecasting with 90%+ accuracy',
      'Automated renewal workflows and outreach',
      'Partner contribution analytics by tier',
      'Upsell and cross-sell signal surfacing',
      'Bi-directional CRM sync (Salesforce, HubSpot)'
    ],

    stats: [
      { v: '+35%', l: 'Renewal Growth' },
      { v: '90%', l: 'Forecast Accuracy' },
      { v: '+25%', l: 'Partner Productivity' }
    ],
    howItWorks: [
      { step: '01', title: 'Data Unification', desc: 'Usage, billing, support, and CRM data are unified into a single customer intelligence graph updated in real time.' },
      { step: '02', title: 'AI Scoring', desc: 'Machine learning models score each account for churn risk, expansion likelihood, and renewal probability on a continuous basis.' },
      { step: '03', title: 'Signal Surfacing', desc: 'High-priority signals are surfaced to the relevant team — renewal alerts to CS, upsell moments to sales, anomalies to finance.' },
      { step: '04', title: 'Action Orchestration', desc: 'Triggered workflows fire automatically — renewal outreach, pricing proposals, or escalations — based on the configured playbooks.' },
    ],
    techSpecs: [
      { label: 'AI model types', value: 'Churn prediction, propensity scoring, price optimisation' },
      { label: 'Dashboard refresh', value: 'Real-time (sub-5 second lag on live metrics)' },
      { label: 'Forecast horizon', value: 'Weekly, monthly, quarterly, and annual rolling views' },
      { label: 'CRM integration', value: 'Salesforce, HubSpot, Dynamics 365 (bi-directional)' },
      { label: 'Reporting formats', value: 'In-app, CSV, PDF, API export, BI tool embedding' },
      { label: 'Data residency', value: 'Regional isolation with SOC 2 and GDPR compliance' },
    ],
    capabilities: [
      { icon: Brain, title: 'AI Recommendation Engine', desc: 'Surfaces the right product, the right offer, and the right moment for each customer based on real usage behaviour patterns.' },
      { icon: TrendingUp, title: 'Revenue Forecasting', desc: 'Predictive models deliver 90%+ accurate pipeline and revenue forecasts with configurable confidence intervals and scenario modelling.' },
      { icon: RefreshCw, title: 'Renewal Automation', desc: 'Accounts approaching renewal are scored, prioritised, and acted on automatically — from outreach to quote generation and contract send.' },
      { icon: Network, title: 'Ecosystem Analytics', desc: 'Partner contribution dashboards show revenue by tier, region, product, and deal type with drill-down to individual transaction level.' },
    ],
    faqs: [
      { q: 'How does the AI churn model work?', a: 'The model ingests usage frequency, support ticket volume, payment behaviour, and engagement signals. It scores each account daily and flags deteriorating accounts before renewal conversations become reactive.' },
      { q: 'Can we customise the renewal workflow triggers?', a: 'Yes. Playbooks are fully configurable — you define the trigger conditions, the outreach sequence, the approval gates, and the escalation paths for each customer segment.' },
      { q: 'How is AI model accuracy maintained as our business evolves?', a: 'Models are retrained automatically on a rolling window of your actual outcome data. Accuracy scores are visible in the admin dashboard and drift alerts fire if performance degrades.' },
    ],
  },
];



/* ─── Sticky nav ────────────────────────────── */

const StickyNav = ({ active }: { active: string }) => (
  <div className="sticky top-[72px] z-40 bg-white/90 backdrop-blur-xl border-b border-black/5 shadow-sm">
    <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto py-2">
      {CARDS.map(c => (
        <a key={c.slug} href={`#${c.slug}`}
          className={`flex-shrink-0 px-4 py-2 rounded-full text-[9px] font-black uppercase tracking-widest transition-all duration-200 ${active === c.slug ? 'bg-[#ff5d22] text-white shadow-md shadow-[#ff5d22]/20' : 'text-black/50 hover:text-black hover:bg-slate-100'}`}>
          {c.title}
        </a>
      ))}
    </div>
  </div>
);



/* ─── Card section ──────────────────────────── */

const CardSection = ({ card, idx }: { card: typeof CARDS[0]; idx: number }) => {
  const isEven = idx % 2 === 0;
  const Icon = card.icon;

  return (
    <section id={card.slug} className={`relative overflow-hidden scroll-mt-[120px] ${isEven ? 'bg-white' : 'bg-slate-950'}`}>
      <style>{`@keyframes caOrbFloat{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(34px,-24px) scale(1.06)}66%{transform:translate(-18px,15px) scale(.95)}}`}</style>

      <div className={`absolute inset-0 ${isEven ? 'bg-[radial-gradient(rgba(0,0,0,0.022)_1px,transparent_1px)] bg-[length:28px_28px]' : 'bg-[radial-gradient(rgba(255,255,255,0.028)_1px,transparent_1px)] bg-[length:28px_28px]'}`} />
      {!isEven && <ParticleCanvas color="#ff5d22" count={38} />}
      <Orb style={{ top: '-4%', right: '-5%' }} color="#ff5d22" size={isEven ? 230 : 310} dur={isEven ? 19 : 23} />
      {!isEven && <Orb style={{ bottom: '-8%', left: '-4%' }} color="#3b82f6" size={250} dur={25} delay={4} />}

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-14">

        {/* ── hero text + stats ── */}
        <div className={`grid lg:grid-cols-2 gap-10 items-center mb-10`}>
          <div className={isEven ? '' : 'order-last lg:order-first'}>
            <Reveal>
              <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest mb-4 border ${isEven ? 'bg-[#ff5d22]/8 border-[#ff5d22]/20 text-[#ff5d22]' : 'bg-white/10 border-white/20 text-white/70'}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#ff5d22]" />
                Core Advantage · {String(idx + 1).padStart(2, '0')}
              </span>
            </Reveal>

            <Reveal delay={0.07}>
              <h2 className={`text-3xl md:text-4xl font-black tracking-tighter leading-[1.08] mb-3 ${isEven ? 'text-slate-950' : 'text-white'}`}>
                {card.title.split(' ').slice(0, -1).join(' ')}{' '}
                <span className="bg-gradient-to-r from-[#ff5d22] to-red-500 bg-clip-text text-transparent">
                  {card.title.split(' ').slice(-1)[0]}
                </span>
              </h2>
            </Reveal>

            <Reveal delay={0.11}>
              <p className={`text-sm font-semibold mb-4 ${isEven ? 'text-slate-500' : 'text-white/60'}`}>{card.tagline}</p>
            </Reveal>

            {/* tags */}
            <Reveal delay={0.13}>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {card.tags.map((tag, i) => (
                  <span key={i} className="text-[9px] uppercase tracking-[0.15em] font-black px-2.5 py-1 rounded-full border border-[#ff5d22]/20 text-[#ff5d22] bg-[#ff5d22]/[0.04]">
                    {tag}
                  </span>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <p className={`text-sm leading-relaxed mb-6 ${isEven ? 'text-slate-600' : 'text-white/70'}`}>{card.overview}</p>
            </Reveal>

            <Reveal delay={0.18}>
              <ul className="space-y-2 mb-6">
                {card.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-md bg-[#ff5d22]/12 border border-[#ff5d22]/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle size={10} className="text-[#ff5d22]" />
                    </div>
                    <span className={`text-[13px] font-medium leading-snug ${isEven ? 'text-slate-700' : 'text-white/75'}`}>{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.22}>
              <div className="flex flex-wrap gap-2">
                <Link to="/contact" className="px-6 py-2.5 rounded-lg bg-[#ff5d22] text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#d41820] transition-all shadow-lg hover:shadow-xl hover:shadow-[#ff5d22]/20 flex items-center gap-2">
                  Book a Demo <ArrowRight size={12} />
                </Link>                
              </div>
            </Reveal>
          </div>

          {/* stat cards + preview box */}
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              {card.stats.map((s, i) => (
                <Reveal key={i} delay={0.1 + i * .08}>
                  <TCard className={`rounded-2xl p-3 border text-center relative overflow-hidden ${isEven ? 'bg-white border-black/8 shadow-sm hover:shadow-md' : 'bg-white/5 border-white/10'}`}>
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/50 to-transparent" />
                    <div className="text-xl font-black text-[#ff5d22] leading-none">{s.v}</div>
                    <div className={`text-[8px] font-bold uppercase tracking-wide mt-1.5 leading-tight ${isEven ? 'text-slate-500' : 'text-white/50'}`}>{s.l}</div>
                  </TCard>
                </Reveal>
              ))}
            </div>

            {/* how-it-works preview */}
            <Reveal delay={0.26}>
              <div className={`rounded-2xl p-4 border relative overflow-hidden ${isEven ? 'bg-gradient-to-br from-slate-50 to-white border-black/8 shadow-sm' : 'bg-gradient-to-br from-slate-900 to-slate-950 border-[#ff5d22]/20'}`}>
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/55 to-transparent" />
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-xl bg-[#ff5d22]/12 border border-[#ff5d22]/25 flex items-center justify-center">
                    {/* ✅ Icon is now always defined for all cards */}
                    <Icon size={17} className="text-[#ff5d22]" />
                  </div>
                  <div className={`text-sm font-black ${isEven ? 'text-black' : 'text-white'}`}>{card.title}</div>
                </div>
                <div className="space-y-1.5">
                  {card.howItWorks.slice(0, 3).map((h, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded text-[9px] font-black flex-shrink-0 flex items-center justify-center ${isEven ? 'bg-[#ff5d22]/10 text-[#ff5d22]' : 'bg-[#ff5d22]/20 text-[#ff5d22]'}`}>{i + 1}</span>
                      <span className={`text-xs font-medium ${isEven ? 'text-slate-600' : 'text-white/65'}`}>{h.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <div>{card.faqs.slice(0, 2).map((faq, i) => <FaqItem key={i} q={faq.q} a={faq.a} i={i} />)}</div>
          </div>
        </div>

        {/* ── how it works ── */}
        <Reveal delay={0.1}>
          <div className={`rounded-3xl p-6 border mb-10 ${isEven ? 'bg-slate-50 border-black/6' : 'bg-white/4 border-white/10'}`}>
            <div className={`text-[9px] font-black uppercase tracking-widest mb-5 ${isEven ? 'text-black/40' : 'text-white/40'}`}>How It Works</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {card.howItWorks.map((h, i) => (
                <div key={i} className={`relative p-4 rounded-2xl border ${isEven ? 'bg-white border-black/6 shadow-sm' : 'bg-white/5 border-white/10'}`}>
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/40 to-transparent" />
                  <div className="text-[#ff5d22] text-[10px] font-black font-mono mb-2">{h.step}</div>
                  <div className={`text-sm font-black mb-1.5 ${isEven ? 'text-black' : 'text-white'}`}>{h.title}</div>
                  <div className={`text-xs leading-relaxed font-medium ${isEven ? 'text-slate-500' : 'text-white/55'}`}>{h.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* ── capabilities ── */}
        <Reveal delay={0.13}>
          <div className="mb-10">
            <div className={`text-[9px] font-black uppercase tracking-widest mb-4 ${isEven ? 'text-black/40' : 'text-white/40'}`}>Key Capabilities</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {card.capabilities.map((c, i) => (
                <TCard key={i} className={`rounded-2xl p-4 border group transition-all duration-300 ${isEven ? 'bg-white border-black/6 shadow-sm hover:border-[#ff5d22]/25 hover:shadow-lg' : 'bg-white/4 border-white/10 hover:bg-white/8'}`}>
                  <div className="w-9 h-9 rounded-xl bg-[#ff5d22]/10 border border-[#ff5d22]/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                    <c.icon size={16} className="text-[#ff5d22]" />
                  </div>
                  <div className={`text-sm font-black mb-1.5 ${isEven ? 'text-black' : 'text-white'}`}>{c.title}</div>
                  <div className={`text-xs leading-relaxed font-medium ${isEven ? 'text-slate-500' : 'text-white/55'}`}>{c.desc}</div>
                </TCard>
              ))}
            </div>
          </div>
        </Reveal>

        {/* ── tech specs ── */}
        <Reveal delay={0.16}>
          <div className={`rounded-3xl border overflow-hidden ${isEven ? 'border-black/6' : 'border-white/10'}`}>
            <div className={`px-5 py-3 border-b ${isEven ? 'bg-slate-50 border-black/6' : 'bg-white/5 border-white/10'}`}>
              <div className={`text-[9px] font-black uppercase tracking-widest ${isEven ? 'text-black/40' : 'text-white/40'}`}>Technical Specifications</div>
            </div>
            <div className={isEven ? 'bg-white' : 'bg-white/2'}>
              {card.techSpecs.map((t, i) => (
                <div key={i} className={`flex items-center justify-between px-5 py-2.5 text-sm ${i < card.techSpecs.length - 1 ? (isEven ? 'border-b border-black/4' : 'border-b border-white/6') : ''}`}>
                  <span className={`font-black text-[10px] uppercase tracking-wider ${isEven ? 'text-black/40' : 'text-white/40'}`}>{t.label}</span>
                  <span className={`font-semibold text-[12px] text-right max-w-xs ${isEven ? 'text-slate-700' : 'text-white/75'}`}>{t.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/18 to-transparent" />
    </section>
  );
};



/* ─── Page ──────────────────────────────────── */

export default function CoreAdvantagePage() {
  const [activeSlug, setActiveSlug] = useState(CARDS[0].slug);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    CARDS.forEach(c => {
      const el = document.getElementById(c.slug);
      if (!el) return;
      const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setActiveSlug(c.slug); }, { rootMargin: '-38% 0px -55% 0px' });
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  return (
    <div className="bg-white overflow-x-hidden">
      <style>{`@keyframes caOrbFloat{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(34px,-24px) scale(1.06)}66%{transform:translate(-18px,15px) scale(.95)}} @keyframes caPulse{0%,100%{opacity:.6;transform:scale(1)}50%{opacity:1;transform:scale(1.15)}}`}</style>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden px-6 pt-20 pb-6 bg-white">
        <div className="absolute inset-0 bg-[radial-gradient(rgba(0,0,0,0.026)_1px,transparent_1px)] bg-[length:26px_26px]" />
        <ParticleCanvas color="#ff5d22" count={48} />
        <Orb style={{ top: '-8%', right: '-5%' }} color="#ff5d22" size={360} dur={18} />
        <Orb style={{ bottom: '-12%', left: '-4%' }} color="#3b82f6" size={280} dur={22} delay={3} />

        <div className="relative z-20 max-w-7xl mx-auto w-full">

  {/* breadcrumb */}
  <Reveal>
    <div className="flex items-center gap-2 mb-4">
      <Link to="/" className="text-[9px] font-black uppercase tracking-widest text-black/35 hover:text-[#ff5d22] transition-colors flex items-center gap-1">
        <ArrowLeft size={10} /> Home
      </Link>
      <span className="text-black/20 text-[9px]">/</span>
      <span className="text-[9px] font-black uppercase tracking-widest text-[#ff5d22]">Core Advantage</span>
    </div>
  </Reveal>

  {/* two-column */}
  <div className="grid lg:grid-cols-2 gap-8 items-start mb-5">

    {/* LEFT */}
    <div>
      <Reveal delay={0.04}>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/6 text-slate-700 text-[9px] font-black uppercase tracking-widest mb-3 border border-slate-200">
          <span className="w-2 h-2 rounded-full bg-[#ff5d22]" style={{ animation: 'caPulse 2s ease infinite' }} />
          Platform · Core Advantage
        </span>
      </Reveal>

      <Reveal delay={0.08}>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.06] text-slate-950 mb-3">
          Cloud Commerce &
          <span className="bg-gradient-to-r from-[#ff5d22] to-red-500 bg-clip-text text-transparent">
            {' '}Revenue Platform
          </span>
        </h1>
      </Reveal>

      <Reveal delay={0.11}>
        <p className="text-sm text-slate-600 font-medium leading-relaxed max-w-lg mb-4">
          Purpose-built for cloud distributors, telecom operators, UCaaS & CCaaS providers, and SaaS aggregators. AppGallop unifies cloud commerce, subscription billing, partner operations, and revenue intelligence into a single platform.
        </p>
      </Reveal>

      {/* live KPI pills */}
      <Reveal delay={0.14}>
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { label: 'Cloud SKUs', value: '5,000+', color: 'emerald' },
            { label: 'Service Activation', value: '<60 sec', color: 'blue' },
            { label: 'Billing Accuracy', value: '99.999%', color: 'red' },
          ].map((p, i) => (
            <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-[10px] font-black ${p.color === 'emerald' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : p.color === 'blue' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-[#E89951]/10 border-[#E89951]/25 text-[#E89951]'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${p.color === 'emerald' ? 'bg-emerald-500' : p.color === 'blue' ? 'bg-blue-500' : 'bg-[#E89951]'}`} style={{ animation: 'caPulse 2.5s ease infinite' }} />
              <span className="opacity-60 font-bold">{p.label}</span>
              <span>{p.value}</span>
            </div>
          ))}
        </div>
      </Reveal>

      {/* jump-to list */}
      <Reveal delay={0.17}>
        <div className="space-y-1.5">
          {CARDS.map((c, i) => (
            <a key={c.slug} href={`#${c.slug}`}
              className="group flex items-center gap-3 p-2.5 rounded-xl border border-black/8 bg-white/80 hover:border-[#ff5d22]/30 hover:shadow-md hover:shadow-[#ff5d22]/6 transition-all duration-300">
              <div className="w-8 h-8 rounded-lg bg-[#ff5d22]/8 border border-[#ff5d22]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#ff5d22] group-hover:border-[#ff5d22] transition-all duration-300">
                <c.icon size={14} className="text-[#ff5d22] group-hover:text-white transition-colors duration-300" />
              </div>
              <div className="flex-grow min-w-0">
                <div className="text-[8px] font-black uppercase tracking-widest text-black/30 mb-0.5">Module {String(i + 1).padStart(2, '0')}</div>
                <div className="text-xs font-black text-black group-hover:text-[#ff5d22] transition-colors">{c.title}</div>
                <div className="text-[10px] text-black/40 font-medium truncate">{c.tagline}</div>
              </div>
              <div className="flex-shrink-0 text-[8px] font-black uppercase tracking-widest text-[#ff5d22]/50 group-hover:text-[#ff5d22] transition-colors flex items-center gap-1">
                Explore <ArrowRight size={10} />
              </div>
            </a>
          ))}
        </div>
      </Reveal>
    </div>

    {/* RIGHT: platform overview panel */}
    <Reveal delay={0.13}>
      <div className="relative">
        {/* background glow */}
        <div className="absolute -inset-4 rounded-[34px] bg-slate-200/70 blur-2xl" />
        <div className="relative overflow-hidden rounded-[24px] border border-black/8 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.12)] sm:rounded-[28px]">
          <div className="flex items-center gap-1.5 border-b border-black/6 bg-slate-50 px-4 py-3">
            <span className="h-2 w-2 rounded-full bg-slate-300" />
            <span className="h-2 w-2 rounded-full bg-slate-300" />
            <span className="h-2 w-2 rounded-full bg-[#ff5d22]/70" />
            <div className="ml-2 flex-1 rounded-md border border-black/6 bg-white px-3 py-1 text-[9px] font-mono text-slate-400">
              app.appgallop.com / platform-overview
            </div>
          </div>
          <div className="p-4 sm:p-5">

          {/* Header */}
          <div className="relative z-10 mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.18em] text-slate-400 font-black">
                AppGallop Platform
              </div>
              <h3 className="mt-1 text-lg font-black text-slate-950">
                Digital commerce dashboard
              </h3>
              <p className="mt-1 text-[11px] font-semibold text-slate-500">
                Marketplace, billing, partner operations, and revenue control.
              </p>
              <p className="hidden">
                Marketplace • Billing • Revenue Intelligence
              </p>
            </div>
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="text-[10px] font-black text-emerald-700">Operational</span>
            </div>
          </div>

          {/* Ecosystem Visual — tightened from h-[340px] to h-[220px] */}
          <div className="relative z-10 mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              { value: "$12.4M", label: "GMV" },
              { value: "250+", label: "Partners" },
              { value: "98.7%", label: "Renewal" },
              { value: "50+", label: "Integrations" }
            ].map((item, index) => (
              <div key={item.label} className="min-w-0 rounded-lg border border-black/8 bg-slate-50 p-3">
                <div className={`text-base font-black leading-none ${index === 0 ? "text-[#ff5d22]" : "text-slate-950"}`}>
                  {item.value}
                </div>
                <div className="mt-1 truncate text-[8px] font-black uppercase tracking-[0.12em] text-slate-400">
                  {item.label}
                </div>
              </div>
            ))}
          </div>

          <div className="relative z-10 grid gap-3 md:grid-cols-[1.15fr_0.85fr]">
            <div className="rounded-lg border border-black/8 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="text-[9px] font-black uppercase tracking-[0.16em] text-slate-400">Revenue Flow</div>
                  <div className="mt-1 text-sm font-black text-slate-950">Lifecycle throughput</div>
                </div>
                <div className="rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/8 px-2.5 py-1 text-[9px] font-black text-[#ff5d22]">
                  +18.2%
                </div>
              </div>

              <div className="space-y-3">
                {[
                  { label: "Catalog to order", value: "84%", width: "84%" },
                  { label: "Order to invoice", value: "91%", width: "91%" },
                  { label: "Renewal coverage", value: "76%", width: "76%" }
                ].map((row) => (
                  <div key={row.label}>
                    <div className="mb-1.5 flex items-center justify-between text-[10px] font-bold">
                      <span className="text-slate-500">{row.label}</span>
                      <span className="text-slate-900">{row.value}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-[#ff5d22]" style={{ width: row.width }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-black/8 bg-slate-50 p-4">
              <div className="mb-3 text-[9px] font-black uppercase tracking-[0.16em] text-slate-400">
                Active Modules
              </div>
              <div className="space-y-2">
                {[
                  { icon: ShoppingCart, label: "Marketplace", meta: "Live catalog sync" },
                  { icon: CreditCard, label: "Billing Core", meta: "Invoice queue clear" },
                  { icon: BarChart3, label: "Revenue Intelligence", meta: "Forecast updated" }
                ].map(({ icon: Icon, label, meta }) => (
                  <div key={label} className="flex items-center gap-3 rounded-lg border border-black/6 bg-white p-2.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#ff5d22]/10 text-[#ff5d22]">
                      <Icon size={15} />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-[11px] font-black text-slate-900">{label}</div>
                      <div className="truncate text-[9px] font-semibold text-slate-400">{meta}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="relative z-10 mt-3 rounded-lg border border-black/8 bg-slate-50 p-3">
            <div className="grid grid-cols-[1fr_auto] items-center gap-3 text-[10px] font-semibold text-slate-500">
              <div className="min-w-0 truncate">Next settlement batch</div>
              <div className="font-black text-slate-950">12 min</div>
            </div>
          </div>

        </div>
      </div>
      </div>
    </Reveal>
  </div>

  {/* trust strip */}
  <Reveal delay={0.21}>
    <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-black/6">
      <span className="text-[9px] font-black uppercase tracking-widest text-black/30 mr-1">Part of</span>
      {['Commerce Layer', 'Monetization Layer', 'Operations Layer', 'AI Intelligence', 'ERP Integration', 'Partner PRM'].map(badge => (
        <span key={badge} className="px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-[9px] font-black uppercase tracking-wider text-slate-500">
          {badge}
        </span>
      ))}
    </div>
  </Reveal>

</div>
      </section>

      {/* sticky nav */}
      <StickyNav active={activeSlug} />

      {/* card sections */}
      {CARDS.map((c, i) => <CardSection key={c.slug} card={c} idx={i} />)}

      {/* CTA */}
      <section className="relative bg-slate-950 py-14 px-6 overflow-hidden">
        <ParticleCanvas color="#ff5d22" count={45} />
        <Orb style={{ top: '10%', left: '8%' }} color="#ff5d22" size={380} dur={20} />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
              Three modules. One platform.
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-white mb-4">
              Cloud Commerce. Telecom Billing.
              <span className="bg-gradient-to-r from-[#ff5d22] to-red-400 bg-clip-text text-transparent">
                Revenue Growth.
              </span>
            </h2>
          </Reveal>
          <Reveal delay={0.13}>
            <p className="text-white/65 text-sm leading-relaxed mb-6 max-w-xl mx-auto">
              Discover how AppGallop helps cloud distributors, telecom operators, UCaaS & CCaaS providers, and SaaS ecosystems automate commerce, billing, partner operations, and revenue growth from a single platform.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="flex gap-3 justify-center">
              <Link to="/contact" className="px-8 py-3.5 rounded-lg bg-[#ff5d22] text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#d41820] transition-all shadow-xl hover:shadow-[#ff5d22]/25 flex items-center gap-2">
                Book a Demo <ArrowRight size={14} />
              </Link>              
            </div>
          </Reveal>
        </div>
      </section>

      <ContactSection id="contact" />
    </div>
  );
}
